// Mind2Magic CRM — Excel Import Module
// Extracted from index.html Step 9.

import { STAGES } from '../constants.js'
import { store } from '../store.js'

let importData = null
let currentFileName = ''

// Map Excel destination codes to cruise line names
const DEST_TO_CRUISE_LINE = {
    'DCL': 'Disney Cruise Line',
    'RC': 'Royal Caribbean',
    'MSC': 'MSC Cruises',
    'NCL': 'Norwegian',
    'CARNIVAL': 'Carnival',
    'CELEBRITY': 'Celebrity',
    'VIRGIN': 'Virgin Voyages'
}

function parseCruisePlan(plan, hotel) {
    // Extract ship and port from Plan Preliminar and Hotel text
    // Patterns: "Disney Destiny - Fort Lauderdale (4 y 1 año)"
    //           "Disney Dream desde Port Canaveral (2 adultos + Kai (4))"
    //           "MSC Divina desde Miami"
    //           "Disney Destiny - 4 adultos + 1 niña 6 años" (dash before travelers, not port)
    //           "MSC Seashore" (ship only, port in Hotel: "Desde Puerto Cañaveral")
    if (!plan && !hotel) return { ship: '', departurePort: '' }
    const text = String(plan || '').trim()
    const hotelText = String(hotel || '').trim()

    let ship = '', departurePort = ''

    // Pattern 1: "Ship desde Port (travelers...)"
    const desdeMatch = text.match(/^(.+?)\s+desde\s+([^(]+)/i)
    if (desdeMatch) {
        ship = desdeMatch[1].trim()
        departurePort = desdeMatch[2].trim()
    }
    // Pattern 2: "Ship - Port (travelers...)" — only if text after dash starts with a letter (not a number/travelers)
    if (!ship) {
        const dashMatch = text.match(/^(.+?)\s+-\s+([^(]+)/i)
        if (dashMatch) {
            const afterDash = dashMatch[2].trim()
            // If after dash starts with a digit, it's travelers not port (e.g. "4 adultos")
            if (!/^\d/.test(afterDash)) {
                ship = dashMatch[1].trim()
                departurePort = afterDash
            } else {
                // Ship only, no port in plan (e.g. "Disney Destiny - 4 adultos...")
                ship = dashMatch[1].trim()
            }
        }
    }
    // Pattern 3: Ship name only (no dash, no desde) — e.g. "MSC Seashore"
    if (!ship && text && !text.toLowerCase().startsWith('crucero')) {
        ship = text.replace(/\s*\(.*\)$/, '').trim()
    }

    // Extract port from Hotel column if not found in plan
    // e.g. "Desde Puerto Cañaveral", "Desde Fort Lauderdale"
    if (!departurePort && hotelText) {
        const hotelPortMatch = hotelText.match(/desde\s+(.+?)(?:\s*[-–]\s|\s*\d|$)/i)
        if (hotelPortMatch) departurePort = hotelPortMatch[1].trim()
    }

    return { ship, departurePort }
}

function buildCruiseFromExcel(record) {
    if (record.type !== 'crucero' && record.type !== 'parques_crucero') return null
    const dest = String(record.destination || '').toUpperCase().trim()
    const line = DEST_TO_CRUISE_LINE[dest] || ''
    const { ship, departurePort } = parseCruisePlan(record.planPreliminar, record.hotel)
    if (!line && !record.reservationNum && !ship) return null
    return {
        line,
        ship,
        reservationNum: record.reservationNum || '',
        departurePort,
        departureDate: '',
        returnDate: '',
        cabin: '',
        stateroomNum: '',
        deck: '',
        cost: record.totalVenta || null,
        notes: ''
    }
}

// Map Excel destination values to CRM park names
const DEST_TO_PARK = {
    'WDW': 'Disney World', 'DISNEY WORLD': 'Disney World', 'DISNEY': 'Disney World',
    'UNIVERSAL': 'Universal Orlando', 'UNIVERSAL ORLANDO': 'Universal Orlando',
    'UNIVERSAL HOLLYWOOD': 'Universal Hollywood',
    'DISNEYLAND': 'Disneyland', 'DISNEYLAND PARIS': 'Disneyland',
    'LEGOLAND': 'Legoland', 'SEAWORLD': 'SeaWorld',
    'ORLANDO': 'Disney World' // default assumption for generic "Orlando"
}

function parseParkFromExcel(record) {
    // Build parkEntries[] from Detalle, Plan Preliminar, Destination, and Hotel
    if (record.type === 'crucero' || record.type === 'hotel' || record.type === 'evento') return []

    const det = String(record.detalle || '').trim()
    const plan = String(record.planPreliminar || '').trim()
    const dest = String(record.destination || '').toUpperCase().trim()
    const hotel = String(record.hotel || '').trim()

    // Determine park brand from destination
    const parkName = DEST_TO_PARK[dest] || ''
    if (!parkName && !det && !plan) return []

    const isDisney = parkName === 'Disney World' || parkName === 'Disneyland'
    const isUniversal = parkName === 'Universal Orlando' || parkName === 'Universal Hollywood'

    const entries = []
    const detLower = det.toLowerCase()
    const planLower = plan.toLowerCase()

    // --- Extract park days count ---
    let days = null

    // Pattern: "N Parques" or "N parks" in Detalle
    const daysDet = det.match(/(\d+)\s*(?:parques|parks|Parques)/i)
    if (daysDet) days = parseInt(daysDet[1])

    // Pattern: "Hotel + N Parques" in Detalle
    if (!days) {
        const hotelParques = det.match(/\+\s*(\d+)\s*(?:parques|parks)/i)
        if (hotelParques) days = parseInt(hotelParques[1])
    }

    // Pattern: "N días" or "N day" in Detalle or Plan
    if (!days) {
        const diasDet = det.match(/(\d+)\s*(?:días|dias|day|Day)/i)
        if (diasDet) days = parseInt(diasDet[1])
        if (!days) {
            const diasPlan = plan.match(/(\d+)\s*(?:días|dias|day|Day)\s*(?:parque|park)?/i)
            if (diasPlan) days = parseInt(diasPlan[1])
        }
    }

    // Pattern: Expedia ticket "N-Day" in Detalle
    if (!days) {
        const expedia = det.match(/(\d+)-Day/i)
        if (expedia) days = parseInt(expedia[1])
    }

    // --- Extract dining plan ---
    const allText = detLower + ' ' + planLower
    let diningPlan = ''
    if (allText.includes('qs') || allText.includes('quick service')) diningPlan = 'quick_service'
    else if (allText.includes(' dp') || allText.includes('+dp') || allText.includes('dining plan') || allText.includes('plan de comida')) diningPlan = 'standard'
    else if (allText.includes(' ts') || allText.includes('disney dining')) diningPlan = 'standard'

    // --- Extract Park Hopper ---
    const parkHopper = allText.includes('hopper') || allText.includes('park to park')

    // --- Extract Express Pass ---
    const expressPass = allText.includes('express')

    // --- Extract ticket type ---
    let ticketType = ''
    if (parkHopper) ticketType = isUniversal ? 'Park to Park' : 'Park Hopper'
    else if (det.match(/\d+-Day.*Base/i)) {
        // Expedia-style: "1-Day, 1-Park Base PLUS..."
        ticketType = det.replace(/\s*\(.*\)$/, '').trim()
    }

    // --- Check for Epic Universe ---
    const hasEpic = allText.includes('epic')

    // --- Detect multiple park brands (disney_universal type) ---
    if (record.type === 'disney_universal' || record.type === 'parques_crucero') {
        // Build separate entries per brand
        if (isDisney || detLower.includes('disney') || detLower.includes('mk') || detLower.includes('epcot')) {
            entries.push({
                park: 'Disney World', days: days, ticketType: '', validFrom: null, validTo: null,
                reservationNum: '', parkHopper: parkHopper && !isUniversal, diningPlan, lightningLane: false,
                expressPass: false, cost: null
            })
        }
        if (isUniversal || detLower.includes('universal') || hasEpic) {
            entries.push({
                park: 'Universal Orlando', days: null, ticketType: parkHopper ? 'Park to Park' : '',
                validFrom: null, validTo: null, reservationNum: '', parkHopper: false,
                diningPlan: '', lightningLane: false, expressPass, cost: null
            })
        }
        return entries
    }

    // --- Single brand entry ---
    const entry = {
        park: parkName || (isDisney ? 'Disney World' : isUniversal ? 'Universal Orlando' : ''),
        days,
        ticketType,
        validFrom: null,
        validTo: null,
        reservationNum: record.reservationNum || '',
        parkHopper: isDisney ? parkHopper : false,
        diningPlan: isDisney ? diningPlan : '',
        lightningLane: false,
        expressPass: isUniversal ? expressPass : false,
        cost: record.totalVenta || null
    }

    // Only create entry if we have meaningful park data
    if (entry.park || days || ticketType || diningPlan) {
        entries.push(entry)
    }

    // Add separate Epic Universe entry if detected alongside regular Universal
    if (hasEpic && entries.length > 0 && entries[0].park === 'Universal Orlando' && !ticketType.toLowerCase().includes('epic')) {
        // Epic is already covered by Park to Park + Epic ticket, don't double
    }

    return entries
}

function handleFile(e) {
    const file = e.target.files[0]
    if (!file) return
    currentFileName = file.name
    const reader = new FileReader()
    reader.onload = (evt) => processWorkbook(XLSX.read(evt.target.result, { type: 'binary', cellDates: true, cellStyles: true }))
    reader.readAsBinaryString(file)
}

// Helper para buscar columna con múltiples variaciones de nombre
function findColumn(headers, ...variants) {
    for (const variant of variants) {
        const idx = headers.findIndex(h => {
            const hLower = h.toLowerCase()
            const vLower = variant.toLowerCase()
            return h === variant || hLower === vLower || hLower.includes(vLower) || h.includes(variant)
        })
        if (idx !== -1) return idx
    }
    return -1
}

function processWorkbook(wb) {
    const { leads, clients, historico, team } = store

    // Detect staffing sheets in this workbook
    const thunderSheets = window.detectThunderStaffing(wb)
    const hasTurismoSheets = ['VENTAS 2024', 'VENTAS 2025', 'VENTAS 2026', 'PROSPECTOS 2025'].some(s => wb.SheetNames.includes(s))

    if (thunderSheets && !hasTurismoSheets) {
        // Pure staffing file — route entirely to Thunder import
        window.processThunderImport(thunderSheets, currentFileName || 'Thunder Staffing')
        return
    }

    if (thunderSheets && hasTurismoSheets) {
        // Mixed file — auto-import staffing sheets to empresas silently
        window.autoImportThunderSheets(thunderSheets, currentFileName || 'Excel Mixto')
        // Continue below to process turismo sheets normally
    }

    const ventasRecords = []
    const prospectosRecords = []

    // Procesar hojas de VENTAS
    ;['VENTAS 2024', 'VENTAS 2025', 'VENTAS 2026'].forEach(sheetName => {
        if (!wb.SheetNames.includes(sheetName)) return
        const ws = wb.Sheets[sheetName]
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })
        let headerRow = -1
        for (let i = 0; i < Math.min(10, data.length); i++) {
            if (data[i] && data[i].some(c => String(c || '').includes('Client'))) { headerRow = i; break }
        }
        if (headerRow === -1) return
        // Normalizar headers: quitar espacios especiales (non-breaking space \u00a0)
        const headers = data[headerRow].map(h => String(h || '').trim().replace(/\s+/g, ' ').replace(/\u00a0/g, ' '))

        // Log para debug (se puede quitar después)
        console.log(`[${sheetName}] Headers encontrados:`, headers)

        // Mapeo flexible de columnas con múltiples variantes de nombres
        const colMap = {
            name: findColumn(headers, 'Client Name', 'Client', 'Nombre', 'Cliente'),
            email: findColumn(headers, 'Email', 'Correo', 'E-mail'),
            phone: findColumn(headers, 'Celular', 'Teléfono', 'Phone', 'Tel'),
            booking: findColumn(headers, 'Booking Date', 'Booking', 'Fecha Reserva'),
            travel: findColumn(headers, 'Travel Date', 'Fecha Viaje', 'Fecha de Viaje', 'Check-in'),
            travelEnd: findColumn(headers, 'Travel End', 'Fecha Fin', 'Check-out', 'Fecha Salida'),
            resNum: findColumn(headers, 'Reservation #', 'Reservation', '# Reserva', 'Confirmación', 'Confirmation'),
            detalle: findColumn(headers, 'Detalle del Plan', 'Detalle', 'Plan', 'Descripción'),
            plan: findColumn(headers, 'Plan Preliminar', 'Plan'),
            hotel: findColumn(headers, 'Hotel', 'Alojamiento'),
            dest: findColumn(headers, 'Destination', 'Destino'),
            platform: findColumn(headers, 'Platform', 'Plataforma', 'Fuente'),
            totalVenta: findColumn(headers, 'Total Venta', 'Venta', 'Total', 'Precio'),
            commission: findColumn(headers, 'Total Commission', 'Commission', 'Comisión', 'Comision'),
            commission70: findColumn(headers, '70% Commission', '70%\u00a0Commission', '70%', '70', 'M2M', 'Comisión 70'),
            fechaPagoFinal: findColumn(headers, 'Fecha Pago Final', 'Pago Final', 'Final Payment', 'Payment Due', 'Deadline')
        }

        // Buscar columnas de Abono - pueden tener varios nombres y ser múltiples
        const abonoVariants = ['Abono', 'Abono 1', 'Abono 2', '1er Abono', '2do Abono', 'Primer Abono', 'Segundo Abono', 'Depósito', 'Deposit']
        const fechaAbonoVariants = ['Fecha Abono', 'Fecha Primer Abono', 'Fecha 1er Abono', 'Fecha Pago Abono', 'Fecha Depósito']

        const abonoIdx = []
        const fechaAbonoIdx = []

        headers.forEach((h, i) => {
            const hLower = h.toLowerCase()
            // Detectar columnas de abono (valor)
            if (abonoVariants.some(v => h === v || hLower === v.toLowerCase())) {
                abonoIdx.push(i)
            }
            // Detectar columnas de fecha de abono
            if (fechaAbonoVariants.some(v => h === v || hLower.includes(v.toLowerCase()) || h.includes(v))) {
                fechaAbonoIdx.push(i)
            }
        })

        colMap.abono1 = abonoIdx[0] ?? -1
        colMap.abono2 = abonoIdx[1] ?? -1
        colMap.fechaAbono1 = fechaAbonoIdx[0] ?? -1
        colMap.fechaAbono2 = fechaAbonoIdx[1] ?? -1

        console.log(`[${sheetName}] Column mapping:`, colMap)

        // Helper: check if row is canceled — columns B(1) through V(21) must ALL be red background
        function isRedRow(rowIdx) {
            function isCellRedBg(cell) {
                if (!cell || !cell.s) return false
                const fills = [cell.s.fgColor, cell.s.bgColor, cell.s.patternColor, cell.s.fill?.fgColor, cell.s.fill?.bgColor].filter(Boolean)
                for (const fill of fills) {
                    const rgb = fill.rgb || ''
                    if (rgb) {
                        const r = parseInt(rgb.substring(0, 2), 16)
                        const g = parseInt(rgb.substring(2, 4), 16)
                        const b = parseInt(rgb.substring(4, 6), 16)
                        if (r > 180 && g < 100 && b < 100) return true
                        if (r > 200 && g < 150 && b < 150 && r > g * 1.5) return true
                    }
                    if (fill.indexed != null && [10, 53, 60].includes(fill.indexed)) return true
                    if (fill.theme != null && [5, 9].includes(fill.theme)) return true
                }
                return false
            }

            // Check exactly columns B(1) through V(21) — all 21 must be red
            for (let c = 1; c <= 21; c++) {
                const cellRef = XLSX.utils.encode_cell({ r: rowIdx, c })
                const cell = ws[cellRef]
                if (!isCellRedBg(cell)) return false
            }
            return true
        }

        for (let i = headerRow + 1; i < data.length; i++) {
            const row = data[i]
            if (!row || !row[colMap.name]) continue
            const { titular, contacto } = window.parseClientName(row[colMap.name])
            if (!titular) continue

            // Detect canceled rows (red background in Excel)
            const canceled = isRedRow(i)
            if (canceled) console.log(`[${sheetName}] ❌ Canceled (red): ${titular} (row ${i + 1})`)

            // LÓGICA DE ABONOS (según estructura del Excel de Mind2Magic):
            // - Si Abono1 = "-" y Abono2 = "-" → YA PAGÓ TODO (pago completo)
            // - Si Abono1 = "-" y Abono2 = fecha → YA PAGÓ ABONO, falta saldo (fecha = límite pago final)
            // - Si Abono1 = fecha y Abono2 = fecha → NO HA PAGADO NADA (fechas son límites)

            const abono1Val = colMap.abono1 >= 0 ? row[colMap.abono1] : null
            const abono2Val = colMap.abono2 >= 0 ? row[colMap.abono2] : null

            // Determinar si es "-" (vacío/pagado) o una fecha
            const abono1EsGuion = !abono1Val || abono1Val === '-' || abono1Val === ''
            const abono2EsGuion = !abono2Val || abono2Val === '-' || abono2Val === ''
            const abono1EsFecha = !abono1EsGuion && (abono1Val instanceof Date || (typeof abono1Val === 'number' && abono1Val > 40000))
            const abono2EsFecha = !abono2EsGuion && (abono2Val instanceof Date || (typeof abono2Val === 'number' && abono2Val > 40000))

            let fechaLimiteAbono = null
            let fechaPagoFinal = null
            let estadoPago = 'desconocido' // 'pago_completo', 'abono_pagado', 'nada_pagado'

            if (abono1EsGuion && abono2EsGuion) {
                // Ambos "-" = Ya pagó todo
                estadoPago = 'pago_completo'
            } else if (abono1EsGuion && abono2EsFecha) {
                // Abono1="-", Abono2=fecha = Ya pagó abono, falta saldo
                estadoPago = 'abono_pagado'
                fechaPagoFinal = abono2Val
            } else if (abono1EsFecha && abono2EsFecha) {
                // Ambas fechas = No ha pagado nada
                estadoPago = 'nada_pagado'
                fechaLimiteAbono = abono1Val
                fechaPagoFinal = abono2Val
            } else if (abono1EsFecha && abono2EsGuion) {
                // Abono1=fecha, Abono2="-" = Caso raro, asumimos falta abono
                estadoPago = 'nada_pagado'
                fechaLimiteAbono = abono1Val
            }

            // Si hay columna específica de fecha pago final, usarla como override
            if (colMap.fechaPagoFinal >= 0 && row[colMap.fechaPagoFinal]) {
                const fpf = row[colMap.fechaPagoFinal]
                if (fpf instanceof Date || (typeof fpf === 'number' && fpf > 40000)) {
                    fechaPagoFinal = fpf
                }
            }

            // If canceled (red row), override payment status
            if (canceled) estadoPago = 'cancelado'

            ventasRecords.push({
                source: sheetName, titular, contacto, canceled,
                email: colMap.email >= 0 ? row[colMap.email] : null,
                phone: colMap.phone >= 0 ? window.normalizePhone(row[colMap.phone]) : null,
                travelDate: colMap.travel >= 0 ? row[colMap.travel] : null,
                travelEnd: colMap.travelEnd >= 0 ? row[colMap.travelEnd] : null,
                fechaLimiteAbono: fechaLimiteAbono,
                fechaPagoFinal: fechaPagoFinal,
                estadoPago: estadoPago,
                reservationNum: colMap.resNum >= 0 ? row[colMap.resNum] : null,
                detalle: colMap.detalle >= 0 ? row[colMap.detalle] : null,
                planPreliminar: colMap.plan >= 0 ? row[colMap.plan] : null,
                hotel: colMap.hotel >= 0 ? row[colMap.hotel] : null,
                destination: colMap.dest >= 0 ? row[colMap.dest] : null,
                platform: colMap.platform >= 0 ? row[colMap.platform] : null,
                totalVenta: colMap.totalVenta >= 0 ? parseFloat(row[colMap.totalVenta]) || 0 : 0,
                commission: colMap.commission >= 0 ? parseFloat(row[colMap.commission]) || 0 : 0,
                commission70: colMap.commission70 >= 0 ? parseFloat(row[colMap.commission70]) || 0 : 0,
                type: window.getType(colMap.dest >= 0 ? row[colMap.dest] : '', colMap.detalle >= 0 ? row[colMap.detalle] : '')
            })
        }
    })

    // Procesar PROSPECTOS
    if (wb.SheetNames.includes('PROSPECTOS 2025')) {
        const ws = wb.Sheets['PROSPECTOS 2025']
        const data = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null })
        let headerRow = -1
        for (let i = 0; i < Math.min(10, data.length); i++) {
            if (data[i] && data[i].some(c => String(c || '').includes('Client'))) { headerRow = i; break }
        }
        if (headerRow === -1) headerRow = 6
        const headers = data[headerRow].map(h => String(h || '').trim().replace(/\s+/g, ' ').replace(/\u00a0/g, ' '))
        const colMap = {
            name: headers.findIndex(h => h.includes('Client')),
            status: headers.findIndex(h => h === 'Leads'),
            medio: headers.findIndex(h => h.includes('Medio')),
            email: headers.findIndex(h => h.toLowerCase().includes('mail')),
            contacto: headers.findIndex(h => h === 'Contacto'),
            travel: headers.findIndex(h => h.includes('Travel')),
            destino: headers.findIndex(h => h.includes('Destino')),
            viajeros: headers.findIndex(h => h.includes('Viajeros')),
            obs: headers.findIndex(h => h.includes('Observaciones'))
        }
        for (let i = headerRow + 1; i < data.length; i++) {
            const row = data[i]
            if (!row || !row[colMap.name]) continue
            const { titular, contacto } = window.parseClientName(row[colMap.name])
            if (!titular) continue
            const leadStatus = colMap.status >= 0 ? String(row[colMap.status] || '').toLowerCase() : ''
            if (leadStatus.includes('viajó') || leadStatus.includes('cliente') || leadStatus.includes('compró')) continue
            prospectosRecords.push({
                source: 'PROSPECTOS 2025', titular, contacto,
                email: colMap.email >= 0 ? row[colMap.email] : null,
                phone: colMap.contacto >= 0 ? window.normalizePhone(row[colMap.contacto]) : null,
                travelDate: colMap.travel >= 0 ? row[colMap.travel] : null,
                destination: colMap.destino >= 0 ? row[colMap.destino] : null,
                viajeros: colMap.viajeros >= 0 ? row[colMap.viajeros] : null,
                observaciones: colMap.obs >= 0 ? row[colMap.obs] : null,
                type: window.getType(colMap.destino >= 0 ? row[colMap.destino] : '', ''),
                totalVenta: 0, commission: 0, commission70: 0
            })
        }
    }

    // Análisis para preview
    const byStageVentas = {}
    ventasRecords.forEach(r => { const s = window.determineStageVentas(r); byStageVentas[s] = (byStageVentas[s] || 0) + 1 })
    const uniqueClients = new Set([...ventasRecords, ...prospectosRecords].map(r => r.titular.toLowerCase())).size
    importData = { ventasRecords, prospectosRecords, uniqueClients, byStageVentas }
    showPreview()
}

function showPreview() {
    const { ventasRecords, prospectosRecords, uniqueClients, byStageVentas } = importData
    const preview = document.getElementById('preview')
    const totalValue = ventasRecords.reduce((s, r) => s + (r.totalVenta || 0), 0)
    const totalComm = ventasRecords.reduce((s, r) => s + (r.commission || 0), 0)
    const totalCommM2M = ventasRecords.reduce((s, r) => s + (r.commission70 || 0), 0)
    preview.innerHTML = `
        <h3>Vista previa de importación</h3>
        <div class="preview-stats">
            <div class="preview-stat"><div class="preview-stat-val">${ventasRecords.length}</div><div class="preview-stat-label">Ventas</div></div>
            <div class="preview-stat"><div class="preview-stat-val">${prospectosRecords.length}</div><div class="preview-stat-label">Prospectos</div></div>
            <div class="preview-stat"><div class="preview-stat-val">${uniqueClients}</div><div class="preview-stat-label">Clientes</div></div>
            <div class="preview-stat"><div class="preview-stat-val">${window.fmt(totalValue)}</div><div class="preview-stat-label">Ventas</div></div>
            <div class="preview-stat"><div class="preview-stat-val">${window.fmt(totalComm)}</div><div class="preview-stat-label">Comisión</div></div>
            <div class="preview-stat"><div class="preview-stat-val">${window.fmt(totalCommM2M)}</div><div class="preview-stat-label">M2M</div></div>
        </div>
        <div class="preview-section">
            <h4>Asignación de etapas (Ventas)</h4>
            <div class="preview-list">
                ${Object.entries(byStageVentas).map(([k, v]) => `<div class="preview-item"><span>${STAGES.find(s => s.id === k)?.name || (k === 'historico' ? 'Histórico' : k)}</span><span>${v}</span></div>`).join('')}
            </div>
        </div>
        <div class="preview-section">
            <h4>Prospectos → Nuevo Lead</h4>
            <div class="preview-list"><div class="preview-item"><span>Todos los prospectos</span><span>${prospectosRecords.length}</span></div></div>
        </div>
        <div class="preview-actions">
            <button class="btn btn-secondary" onclick="cancelImport()">Cancelar</button>
            <button class="btn btn-success" onclick="confirmImport()">Confirmar</button>
        </div>`
    preview.classList.remove('hidden')
}

function cancelImport() {
    document.getElementById('preview').classList.add('hidden')
    document.getElementById('fileInput').value = ''
    importData = null
}

function confirmImport() {
    if (!importData) return

    const { ventasRecords } = importData

    // ── DATA INTEGRITY CHECK: detect reservation number conflicts ──
    const resNumMap = {}
    ventasRecords.forEach(r => {
        if (!r.reservationNum) return
        const num = String(r.reservationNum).trim()
        if (!num) return
        const name = window.normalizeForCompare(r.titular)
        if (!resNumMap[num]) resNumMap[num] = []
        if (!resNumMap[num].some(e => e.name === name)) resNumMap[num].push({ name, titular: r.titular, source: r.source })
    })
    const conflicts = Object.entries(resNumMap).filter(([, entries]) => entries.length > 1)
    if (conflicts.length > 0) {
        showResNumConflictModal(conflicts)
        return
    }

    confirmImportAfterConflictCheck()
}

function confirmImportAfterConflictCheck() {
    if (!importData) return

    const { ventasRecords, prospectosRecords } = importData

    // Ventas: only check by reservation number (all rows are legitimate sales)
    const duplicates = []
    const newRecords = []

    ventasRecords.forEach(r => {
        // Primary: match by reservation number
        let existing = r.reservationNum ? window.findDuplicateLead(r.reservationNum) : null
        // Fallback: try name-based matching (catches Typeform leads without reservation#)
        if (!existing) existing = window.findDuplicateLeadMulti(r)
        if (existing) {
            duplicates.push({ record: r, existing: existing })
        } else {
            newRecords.push(r)
        }
    })

    // Prospectos: use multi-field detection (name/phone/date)
    const dupProspectos = []
    const newProspectos = []

    prospectosRecords.forEach(r => {
        const existing = window.findDuplicateLeadMulti(r)
        if (existing) {
            dupProspectos.push({ record: r, existing: existing })
        } else {
            newProspectos.push(r)
        }
    })

    const allDuplicates = [...duplicates, ...dupProspectos]

    // If there are duplicates, show options
    if (allDuplicates.length > 0) {
        showExcelDuplicatesModal(allDuplicates, newRecords, newProspectos)
        return
    }

    // No duplicates, proceed with import
    processExcelImport(ventasRecords, prospectosRecords)
}

function showExcelDuplicatesModal(duplicates, newRecords, prospectosRecords) {
    const modal = document.createElement('div')
    modal.className = 'modal-bg active'
    modal.id = 'excelDuplicatesModal'

    const dupList = duplicates.slice(0, 5).map(d => {
        const ref = d.record.reservationNum ? `<span style="color:var(--purple);font-weight:600;">#${d.record.reservationNum}</span> — ` : ''
        const matchInfo = d.record.reservationNum ? '' : ` <span style="background:var(--yellow);color:#000;padding:1px 6px;border-radius:4px;font-size:10px;font-weight:600;">nombre</span>`
        return `<div style="padding:10px 12px;background:var(--bg-2);border-radius:8px;margin-bottom:6px;font-size:13px;display:flex;align-items:center;gap:6px;">
            <span>${ref}${window.escHtml(d.record.titular)}${matchInfo}</span>
            <span style="color:var(--text-3);font-size:11px;margin-left:auto;">= ${window.escHtml(d.existing.titular)}</span>
        </div>`
    }).join('')

    const moreText = duplicates.length > 5 ? `<p style="font-size:12px;color:var(--text-3);margin-top:4px;">...y ${duplicates.length - 5} más</p>` : ''

    const newCount = newRecords.length + prospectosRecords.length
    const totalCount = duplicates.length + newCount

    modal.innerHTML = `
        <div class="modal" style="max-width:520px;">
            <div class="modal-head">
                <h2 class="modal-title">Duplicados Detectados</h2>
                <button class="modal-close" onclick="closeExcelDuplicatesModal()">&times;</button>
            </div>
            <div class="modal-body" style="max-height:60vh;overflow-y:auto;">
                <p style="margin:0 0 14px;font-size:13px;color:var(--text-2);line-height:1.5;">
                    <strong style="color:var(--text-1);">${duplicates.length}</strong> reservas ya existen en el CRM y
                    <strong style="color:var(--text-1);">${newCount}</strong> son nuevas.
                </p>
                ${dupList}
                ${moreText}
                <div style="margin-top:16px;padding:14px 16px;background:linear-gradient(135deg,rgba(107,78,255,0.06),rgba(67,97,238,0.06));border:1px solid rgba(107,78,255,0.15);border-radius:10px;">
                    <div style="font-size:14px;font-weight:700;color:var(--purple);margin-bottom:6px;">Fusion Inteligente</div>
                    <div style="font-size:12px;color:var(--text-2);line-height:1.6;">
                        Importa los <strong>${newCount} nuevos</strong> + actualiza los <strong>${duplicates.length} existentes</strong>.<br>
                        Excel gana en datos financieros y fechas. CRM conserva contacto, etapa y notas.
                    </div>
                </div>
            </div>
            <div class="modal-foot" style="flex-wrap:wrap;">
                <button class="btn btn-secondary" onclick="closeExcelDuplicatesModal()">Cancelar</button>
                <button class="btn btn-secondary" onclick="importExcelSkipDuplicates()">Solo nuevos (${newCount})</button>
                <button class="btn btn-primary" onclick="importExcelSmartMerge()" style="padding:10px 20px;">Fusion Inteligente (${totalCount})</button>
            </div>
        </div>
    `
    document.body.appendChild(modal)

    // Store data for later use (prospectosRecords is already filtered to new-only)
    window.excelImportData = { duplicates, newRecords, prospectosRecords, allVentas: importData.ventasRecords, allProspectos: importData.prospectosRecords || prospectosRecords }
}

function closeExcelDuplicatesModal() {
    const modal = document.getElementById('excelDuplicatesModal')
    if (modal) modal.remove()
    window.excelImportData = null
}

function showResNumConflictModal(conflicts) {
    const modal = document.createElement('div')
    modal.className = 'modal-bg active'
    modal.id = 'resNumConflictModal'

    const conflictList = conflicts.map(([num, entries]) => {
        const names = entries.map(e =>
            `<span style="font-weight:600;color:var(--text-1);">${window.escHtml(e.titular)}</span>
             <span style="color:var(--text-3);font-size:11px;">${window.escHtml(e.source || '')}</span>`
        ).join('<span style="color:var(--text-3);margin:0 6px;">&</span>')
        return `<div style="padding:12px 14px;background:var(--bg-2);border-radius:8px;margin-bottom:8px;border-left:3px solid var(--red);">
            <div style="font-size:13px;font-weight:700;color:var(--red);margin-bottom:6px;">Reserva #${window.escHtml(num)}</div>
            <div style="font-size:12px;color:var(--text-2);display:flex;align-items:center;flex-wrap:wrap;">${names}</div>
        </div>`
    }).join('')

    modal.innerHTML = `
        <div class="modal" style="max-width:480px;">
            <div class="modal-head">
                <h2 class="modal-title">Reservas Compartidas</h2>
                <button class="modal-close" onclick="document.getElementById('resNumConflictModal')?.remove()">&times;</button>
            </div>
            <div class="modal-body" style="max-height:60vh;overflow-y:auto;">
                <p style="margin:0 0 14px;font-size:13px;color:var(--text-2);line-height:1.5;">
                    <strong style="color:var(--red);">${conflicts.length}</strong> numero(s) de reserva asignados a personas diferentes.
                    Esto puede causar que datos se mezclen entre leads.
                </p>
                ${conflictList}
                <div style="margin-top:14px;padding:12px 14px;background:rgba(245,158,11,0.06);border:1px solid rgba(245,158,11,0.2);border-radius:8px;font-size:12px;color:var(--text-2);line-height:1.6;">
                    <strong style="color:var(--text-1);">Recomendacion:</strong> Corrige los numeros de reserva en el Excel y vuelve a importar.
                    Si no hay error, puedes continuar.
                </div>
            </div>
            <div class="modal-foot">
                <button class="btn btn-secondary" onclick="document.getElementById('resNumConflictModal')?.remove()">Cancelar</button>
                <button class="btn btn-primary" onclick="dismissResNumConflictAndContinue()">Ignorar y Continuar</button>
            </div>
        </div>
    `
    document.body.appendChild(modal)
}

function dismissResNumConflictAndContinue() {
    document.getElementById('resNumConflictModal')?.remove()
    // Continue with normal import flow (skip the conflict check)
    confirmImportAfterConflictCheck()
}

function importExcelSkipDuplicates() {
    const { newRecords, prospectosRecords } = window.excelImportData
    closeExcelDuplicatesModal()
    processExcelImport(newRecords, prospectosRecords)
    window.toast(`✓ Importados ${newRecords.length + prospectosRecords.length} registros nuevos (duplicados omitidos)`, 'success')
}

function importExcelSmartMerge() {
    const { duplicates, newRecords, prospectosRecords } = window.excelImportData
    const { leads, historico } = store
    closeExcelDuplicatesModal()

    window.saveState('fusión inteligente desde Excel')

    let updated = 0

    // Smart merge: update existing leads
    duplicates.forEach(({ record, existing }) => {
        const lead = leads.find(l => l.id === existing.id) || historico.find(l => l.id === existing.id)
        if (!lead) return

        // Excel WINS on financial data (source of truth for bookings)
        if (record.totalVenta) lead.totalVenta = record.totalVenta
        if (record.commission) lead.commission = record.commission
        if (record.commission70) lead.commission70 = record.commission70

        // Excel WINS on travel dates
        if (record.travelDate) lead.travelDate = window.parseExcelDate(record.travelDate)
        if (record.travelEnd) lead.travelEnd = window.parseExcelDate(record.travelEnd)

        // Excel WINS on payment deadlines
        if (record.fechaLimiteAbono) lead.fechaAbono = window.parseExcelDate(record.fechaLimiteAbono)
        if (record.fechaPagoFinal) lead.fechaPagoFinal = window.parseExcelDate(record.fechaPagoFinal)

        // Excel WINS on booking details
        if (record.destination) lead.destination = record.destination
        if (record.reservationNum) lead.reservationNum = record.reservationNum
        if (record.platform) lead.platform = record.platform
        if (record.detalle) lead.detalle = record.detalle
        if (record.hotel) lead.hotel = record.hotel

        // FILL GAPS only — CRM wins if it already has data
        if (!lead.phone && record.phone) lead.phone = record.phone
        if (!lead.email && record.email) lead.email = record.email
        if (!lead.contacto && record.contacto) lead.contacto = record.contacto
        if (!lead.type && record.type) lead.type = record.type

        // Build cruises[] from Excel if lead has none (PDF/form data always wins)
        if ((!lead.cruises || !lead.cruises.length) && (record.type === 'crucero' || record.type === 'parques_crucero')) {
            const excelCruise = buildCruiseFromExcel(record)
            if (excelCruise) {
                if (lead.travelDate) excelCruise.departureDate = lead.travelDate
                if (lead.travelEnd) excelCruise.returnDate = lead.travelEnd
                if (lead.reservationNum) excelCruise.reservationNum = lead.reservationNum
                lead.cruises = [excelCruise]
            }
        }

        // Infer platform from cruise line if not set
        if (!lead.platform && (record.type === 'crucero' || record.type === 'parques_crucero')) {
            const dest = String(record.destination || '').toUpperCase().trim()
            if (DEST_TO_CRUISE_LINE[dest]) lead.platform = DEST_TO_CRUISE_LINE[dest]
        }

        // Build parkEntries[] from Excel if lead has none (PDF/form data always wins)
        if (!lead.parkEntries || !lead.parkEntries.length) {
            const excelParks = parseParkFromExcel(record)
            if (excelParks.length > 0) {
                excelParks.forEach(pe => {
                    if (lead.travelDate && !pe.validFrom) pe.validFrom = lead.travelDate
                    if (lead.travelEnd && !pe.validTo) pe.validTo = lead.travelEnd
                })
                lead.parkEntries = excelParks
            }
        }

        // Abono: recalculate from Excel data, but preserve PDF/CRM data when it's better
        if (record.totalVenta && record.type) {
            const calculatedAbono = window.calcularAbonoEsperado(record.type, record.totalVenta)
            // Only update abono (expected) if lead doesn't already have a specific value from PDF
            if (!lead.abono || lead.abono === 0) lead.abono = calculatedAbono
            if (record.estadoPago === 'pago_completo') lead.abonado = record.totalVenta
            else if (record.estadoPago === 'abono_pagado') {
                // Use lead's existing abono if set (e.g. from PDF), otherwise calculated
                const abonoTarget = lead.abono || calculatedAbono
                // Only update abonado if Excel says MORE was paid (never reduce)
                if (!lead.abonado || abonoTarget > lead.abonado) lead.abonado = abonoTarget
            }
        }

        // Stage: recalculate from Excel data (parse dates first so determineStageVentas gets ISO strings, not Excel serial numbers)
        // Excel is the source of truth for cancelation:
        // - If Excel row IS red (record.canceled) → cancelado
        // - If Excel row is NOT red but lead is in cancelado → UN-cancel and recalculate
        // - If lead is cancelado and this is a PROSPECTOS match (no canceled field) → keep cancelado (don't let prospectos overwrite ventas cancelado)
        const excelSaysCanceled = record.canceled || record.estadoPago === 'cancelado'
        const isVentasRecord = record.source && record.source.startsWith('VENTAS')
        const skipStageCalc = (lead.stage === 'cancelado' && !excelSaysCanceled && !isVentasRecord) || lead.stage === 'no_compro'

        if (!skipStageCalc) {
            const parsedDate = record.travelDate ? window.parseExcelDate(record.travelDate) : null
            const parsedEnd = record.travelEnd ? window.parseExcelDate(record.travelEnd) : null
            const hasNoDate = !parsedDate
            const newStage = window.determineStageVentas({ ...record, travelDate: parsedDate, travelEnd: parsedEnd })

            if (newStage === 'historico' || newStage === 'finalizado' || newStage === 'perdido' || newStage === 'cancelado') {
                // Always move to historico/finalizado/cancelado
                lead.stage = newStage
                lead.progress = window.calcProgress(newStage)
                if (newStage === 'cancelado') lead.wasCanceled = true
            } else if (lead.stage === 'cancelado' && !excelSaysCanceled && isVentasRecord) {
                // UN-CANCEL: lead was falsely canceled, Excel now says row is NOT red
                lead.stage = newStage
                lead.progress = window.calcProgress(newStage)
                delete lead.wasCanceled
                console.log(`[SmartMerge] ✅ Un-canceled: ${lead.titular} → ${newStage}`)
            } else if (hasNoDate && newStage === 'nuevo') {
                // No dates = no booking — force to nuevo regardless of current stage
                lead.stage = 'nuevo'
                lead.progress = 0
            } else {
                // Active leads with dates: only advance, never go back (preserve Sara's pipeline work)
                const currentProgress = window.calcProgress(lead.stage)
                const newProgress = window.calcProgress(newStage)
                if (newProgress > currentProgress) {
                    lead.stage = newStage
                    lead.progress = newProgress
                }
            }
        }

        // Sync updated lead to Supabase
        window.syncLeadToSupabase(lead)
        updated++
    })

    // Cleanup: fix ALL leads with wrong stages (catches leads missed by duplicate matching)
    let cleaned = 0
    leads.forEach(lead => {
        // Cancelado and No Compro stay permanently — manual cleanup only
        if (lead.stage === 'cancelado' || lead.stage === 'no_compro') return
        const hasDate = lead.travelDate && lead.travelDate !== '-'
        if (!hasDate && (lead.stage === 'pago_completo' || lead.stage === 'reserva_abono' || lead.stage === 'documentacion' || lead.stage === 'en_destino' || lead.stage === 'finalizado')) {
            lead.stage = 'nuevo'
            lead.progress = 0
            window.syncLeadToSupabase(lead)
            cleaned++
        }
        if (hasDate) {
            const endDate = lead.travelEnd || lead.travelDate
            const d = new Date(endDate)
            if (!isNaN(d)) {
                const daysFromEnd = Math.floor((d - new Date()) / (1000 * 60 * 60 * 24))
                if (daysFromEnd < -180 && lead.stage !== 'historico') {
                    lead.stage = 'historico'
                    lead.progress = 100
                    window.syncLeadToSupabase(lead)
                    cleaned++
                } else if (daysFromEnd < 0 && daysFromEnd >= -180 && lead.stage !== 'finalizado' && lead.stage !== 'historico' && lead.stage !== 'postventa') {
                    lead.stage = 'finalizado'
                    lead.progress = 100
                    window.syncLeadToSupabase(lead)
                    cleaned++
                } else if (daysFromEnd >= 0 && (lead.stage === 'finalizado' || lead.stage === 'en_destino')) {
                    // Future trip wrongly in finalizado/en_destino — recalculate
                    const newStage = lead.totalVenta > 0 ? 'prereserva' : 'nuevo'
                    lead.stage = newStage
                    lead.progress = window.calcProgress(newStage)
                    window.syncLeadToSupabase(lead)
                    cleaned++
                } else if (daysFromEnd >= 0 && lead.stage === 'nuevo' && lead.totalVenta > 0) {
                    // Lead has future dates + financial data but stuck in nuevo — advance to prereserva
                    lead.stage = 'prereserva'
                    lead.progress = window.calcProgress('prereserva')
                    window.syncLeadToSupabase(lead)
                    cleaned++
                }
            }
        }
    })

    // Import new records
    processExcelImport(newRecords, prospectosRecords)
    const cleanMsg = cleaned > 0 ? `, ${cleaned} corregidos` : ''
    window.toast(`🧠 Fusión: ${updated} actualizados, ${newRecords.length + prospectosRecords.length} nuevos${cleanMsg}`, 'success')
}

function processExcelImport(ventasRecords, prospectosRecords) {
    const { leads, clients, historico, team } = store

    // Guardar estado para undo
    window.saveState('importar Excel')

    const defaultResp = team[0]?.nombre || 'Sara'
    const byName = {}

    // Procesar VENTAS → etapa según estado de pago
    ventasRecords.forEach(r => {
        const key = r.titular.toLowerCase()
        if (!byName[key]) byName[key] = { titular: r.titular, contacto: r.contacto, email: null, phone: null, referido: null, trips: [], createdAt: new Date() }
        const c = byName[key]
        if (r.email && !c.email) c.email = r.email
        if (r.phone && !c.phone) c.phone = r.phone
        if (r.contacto && !c.contacto) c.contacto = r.contacto
        // Parse dates first so determineStageVentas gets ISO strings, not Excel serial numbers
        const parsedTravelDate = window.parseExcelDate(r.travelDate)
        const parsedTravelEnd = window.parseExcelDate(r.travelEnd)
        const stage = window.determineStageVentas({ ...r, travelDate: parsedTravelDate, travelEnd: parsedTravelEnd })

        // Calcular abono esperado según tipo
        const abonoEsperado = window.calcularAbonoEsperado(r.type, r.totalVenta)

        // Determinar cuánto ha abonado basado en estadoPago:
        // - 'pago_completo' → Ya pagó todo (abonado = total)
        // - 'abono_pagado' → Ya pagó el abono (abonado = abonoEsperado)
        // - 'nada_pagado' → No ha pagado nada (abonado = 0)
        let abonado = 0
        if (r.estadoPago === 'pago_completo') {
            abonado = r.totalVenta // Ya pagó todo
        } else if (r.estadoPago === 'abono_pagado') {
            abonado = abonoEsperado // Ya pagó el abono
        }
        // Si 'nada_pagado' o 'desconocido', abonado = 0

        // Parsear fechas
        const fechaAbono = window.parseExcelDate(r.fechaLimiteAbono)
        const fechaPagoFinal = window.parseExcelDate(r.fechaPagoFinal)

        // Build cruise entry from Excel destination code (DCL → Disney Cruise Line, etc.)
        const excelCruise = buildCruiseFromExcel(r)
        if (excelCruise && parsedTravelDate) excelCruise.departureDate = parsedTravelDate
        if (excelCruise && parsedTravelEnd) excelCruise.returnDate = parsedTravelEnd

        // Build park entries from Excel Detalle/Plan/Destination
        const excelParks = parseParkFromExcel(r)
        excelParks.forEach(pe => {
            if (parsedTravelDate && !pe.validFrom) pe.validFrom = parsedTravelDate
            if (parsedTravelEnd && !pe.validTo) pe.validTo = parsedTravelEnd
        })

        // Infer platform from cruise line when not set
        const inferredPlatform = r.platform || (excelCruise?.line || null)

        c.trips.push({
            id: 'trip_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            source: r.source, travelDate: parsedTravelDate, travelEnd: parsedTravelEnd,
            destination: r.destination || 'Sin destino', type: r.type,
            detalle: r.detalle, hotel: r.hotel, reservationNum: r.reservationNum,
            platform: inferredPlatform,
            totalVenta: r.totalVenta || 0, commission: r.commission || 0, commission70: r.commission70 || 0,
            abono: abonoEsperado, abonado: abonado, fechaAbono: fechaAbono, fechaPagoFinal: fechaPagoFinal,
            stage, progress: window.calcProgress(stage), responsable: defaultResp,
            cruises: excelCruise ? [excelCruise] : [],
            parkEntries: excelParks
        })
    })

    // Procesar PROSPECTOS → siempre "nuevo"
    prospectosRecords.forEach(r => {
        const key = r.titular.toLowerCase()
        if (!byName[key]) byName[key] = { titular: r.titular, contacto: r.contacto, email: null, phone: null, referido: null, trips: [], createdAt: new Date() }
        const c = byName[key]
        if (r.email && !c.email) c.email = r.email
        if (r.phone && !c.phone) c.phone = r.phone
        c.trips.push({
            id: 'trip_' + Date.now() + '_' + Math.random().toString(36).substr(2, 5),
            source: r.source, travelDate: r.travelDate, destination: r.destination || 'Sin destino',
            type: r.type, totalVenta: 0, commission: 0, commission70: 0,
            stage: 'nuevo', progress: 0, responsable: defaultResp, viajeros: r.viajeros, observaciones: r.observaciones
        })
    })

    // Crear clientes y leads
    Object.values(byName).forEach(c => {
        const key = c.phone || ('name:' + c.titular.toLowerCase())
        clients[key] = c
        c.trips.forEach(trip => {
            if (trip.stage === 'historico') {
                historico.push({
                    id: trip.id, clientKey: key, titular: c.titular, contacto: c.contacto, phone: c.phone, email: c.email,
                    type: trip.type, stage: 'historico', travelDate: trip.travelDate, travelEnd: trip.travelEnd,
                    destination: trip.destination, totalVenta: trip.totalVenta, commission: trip.commission, commission70: trip.commission70,
                    abono: trip.abono, abonado: trip.abonado, fechaAbono: trip.fechaAbono, fechaPagoFinal: trip.fechaPagoFinal,
                    platform: trip.platform, cruises: trip.cruises || [], parkEntries: trip.parkEntries || [],
                    progress: 100, responsable: trip.responsable, reservationNum: trip.reservationNum, detalle: trip.detalle, createdAt: new Date()
                })
            } else {
                leads.push({
                    id: trip.id, clientKey: key, titular: c.titular, contacto: c.contacto, phone: c.phone, email: c.email,
                    type: trip.type, stage: trip.stage, travelDate: trip.travelDate, travelEnd: trip.travelEnd,
                    destination: trip.destination, totalVenta: trip.totalVenta, commission: trip.commission, commission70: trip.commission70,
                    abono: trip.abono, abonado: trip.abonado, fechaAbono: trip.fechaAbono, fechaPagoFinal: trip.fechaPagoFinal,
                    platform: trip.platform, cruises: trip.cruises || [], parkEntries: trip.parkEntries || [],
                    progress: trip.progress, responsable: trip.responsable, reservationNum: trip.reservationNum, detalle: trip.detalle, createdAt: new Date()
                })
            }
        })
    })

    window.renderAll()
    cancelImport()
    window.toast(`Importación: ${Object.keys(clients).length} clientes, ${leads.length} leads, ${historico.length} históricos`, 'success')
    window.showView('pipeline')

    // Sync bulk import to Supabase
    window.syncBulkImportToSupabase()
}

Object.assign(window, {
    handleFile, processWorkbook, findColumn, showPreview,
    cancelImport, confirmImport, dismissResNumConflictAndContinue,
    showExcelDuplicatesModal, closeExcelDuplicatesModal,
    importExcelSkipDuplicates, importExcelSmartMerge,
    processExcelImport,
})
